import React, {InputHTMLAttributes} from 'react';
import {ErrorType} from '../../utils/common';
import minusIcon from '../../assets/minus.svg';
import plusIcon from '../../assets/plus.svg';
import QuestionTooltip from '../QuestionTooltip';
import {ErrorMessage} from '../../styled/common';
import {
  Wrapper,
  StyledInput,
  Label,
  SpinnerButton,
  SpinnerButtonsWrapper,
  SpinnerIcon,
  InpitSpinnerButtonsWrapper,
  RelativeWrapper,
} from './styled';

type SpinnerButtonsProps = {
  onIncrement?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDecrement?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
};

function SpinnerButtons({onDecrement, onIncrement, className}: SpinnerButtonsProps) {
  return (
    <SpinnerButtonsWrapper className={className}>
      <SpinnerButton onClick={onDecrement as any} type="button">
        <SpinnerIcon src={minusIcon} alt="Increment" />
      </SpinnerButton>
      <SpinnerButton onClick={onIncrement as any} type="button">
        <SpinnerIcon src={plusIcon} alt="Decrement" />
      </SpinnerButton>
    </SpinnerButtonsWrapper>
  );
}

export type InputProps = InputHTMLAttributes<HTMLInputElement> &
  SpinnerButtonsProps & {
    label?: string;
    invalid?: boolean;
    error?: ErrorType;
    hideNumberButtons?: boolean;
    inputMode?:
      | 'text'
      | 'none'
      | 'tel'
      | 'url'
      | 'email'
      | 'numeric'
      | 'decimal'
      | 'search';
    labelTooltip?: string;
  };

const defaultProps: InputProps = {
  onChange: undefined,
  onIncrement: () => {},
  onDecrement: () => {},
  label: '',
  invalid: undefined,
  error: '',
  hideNumberButtons: false,
  inputMode: undefined,
  autoComplete: 'on',
  placeholder: '',
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      onChange,
      className,
      label,
      invalid,
      defaultValue,
      disabled,
      name,
      type,
      onIncrement,
      onDecrement,
      max,
      error,
      hideNumberButtons,
      autoComplete,
      inputMode,
      labelTooltip,
      placeholder,
      ...props
    },
    ref,
  ) => {
    return (
      <Wrapper className={className} disabled={disabled}>
        <Label>
          {label}
          {labelTooltip && <QuestionTooltip content={labelTooltip} />}
        </Label>
        <RelativeWrapper>
          <StyledInput
            ref={ref}
            type={type}
            data-testid="input"
            value={value}
            onChange={onChange}
            invalid={invalid || Boolean(error)}
            disabled={disabled}
            defaultValue={defaultValue}
            aria-label={name}
            name={name}
            max={max}
            hideNumberButtons={hideNumberButtons}
            autoComplete={autoComplete}
            inputMode={inputMode}
            placeholder={placeholder}
            {...props}
          />
          {type === 'number' && !hideNumberButtons && (
            <InpitSpinnerButtonsWrapper>
              <SpinnerButtons onDecrement={onDecrement} onIncrement={onIncrement} />
            </InpitSpinnerButtonsWrapper>
          )}
        </RelativeWrapper>

        {error && (
          <ErrorMessage className="error-message" data-testid={`${name}-error`}>
            {error}
          </ErrorMessage>
        )}
      </Wrapper>
    );
  },
);

Input.defaultProps = defaultProps;
export {Input, SpinnerButtons};
